import { getOrCreateAnonymousId } from './anonymous-id';
import {
  ATTRIBUTION_KEY,
  FIRST_PV_FIRED_KEY,
  INITIAL_REFERRER_KEY,
  INITIAL_URL_KEY,
  SCHEMA_VERSION,
  type StoredAttribution,
  type TouchData,
} from './constants';
import { isAnalyticsEnabled } from './config';
import { getAnalyticsUser, getUserStatus } from './context';
import { ensureDataLayer } from './data-layer';
import { sanitizeAnalyticsHref, sanitizeAnalyticsPath } from './sanitize-location';

const EMPTY_VALUES = new Set<unknown>([null, undefined, '', 'null', 'unknown', 'undefined']);

let lastIdentityKey = '';
let analyticsInitialized = false;

const cleanParams = (params: Record<string, unknown>): Record<string, unknown> => {
  const cleaned: Record<string, unknown> = {};

  Object.entries(params).forEach(([key, value]) => {
    if (!EMPTY_VALUES.has(value)) {
      cleaned[key] = value;
    }
  });

  return cleaned;
};

const getAttributionData = (): StoredAttribution => {
  if (typeof window === 'undefined') {
    return {};
  }

  const stored = localStorage.getItem(ATTRIBUTION_KEY);
  return stored ? (JSON.parse(stored) as StoredAttribution) : {};
};

const getInitialParams = (): { initial_page_url?: string; initial_referrer?: string } => {
  if (typeof window === 'undefined') {
    return {};
  }

  const initialPageUrl = localStorage.getItem(INITIAL_URL_KEY);
  const initialReferrer = localStorage.getItem(INITIAL_REFERRER_KEY);

  return {
    ...(initialPageUrl ? { initial_page_url: initialPageUrl } : {}),
    ...(initialReferrer !== null ? { initial_referrer: initialReferrer } : {}),
  };
};

const getIsFirstPageViewFired = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  return sessionStorage.getItem(FIRST_PV_FIRED_KEY) === '1';
};

const setFirstPageViewFired = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  sessionStorage.setItem(FIRST_PV_FIRED_KEY, '1');
};

const sanitizeTouch = (touch?: TouchData): TouchData | undefined => {
  if (!touch) {
    return undefined;
  }

  const cleaned: TouchData = {};
  Object.entries(touch).forEach(([key, value]) => {
    if (!EMPTY_VALUES.has(value)) {
      cleaned[key] = value;
    }
  });

  return Object.keys(cleaned).length > 0 ? cleaned : undefined;
};

const getGlobalParams = (extra?: Record<string, unknown>): Record<string, unknown> => {
  const user = getAnalyticsUser();
  const params: Record<string, unknown> = {
    event_id: crypto.randomUUID(),
    anonymous_id: getOrCreateAnonymousId(),
    user_status: getUserStatus(user),
    schema_version: SCHEMA_VERSION,
    hostname: window.location.hostname,
    page_url: sanitizeAnalyticsHref(window.location.href),
    page_path: sanitizeAnalyticsPath(window.location.pathname),
    page_title: document.title || undefined,
  };

  if (user?.id) {
    params.backend_user_id = user.id;
  }

  if (document.referrer) {
    params.page_referrer = document.referrer;
  }

  if (extra) {
    Object.entries(extra).forEach(([key, value]) => {
      if (!EMPTY_VALUES.has(value)) {
        params[key] = value;
      }
    });
  }

  return params;
};

const pushToDataLayer = (payload: Record<string, unknown>): void => {
  if (!isAnalyticsEnabled()) {
    return;
  }

  ensureDataLayer();

  try {
    window.dataLayer.push(cleanParams(payload));
  } catch (error) {
    console.error('[Analytics] DataLayer push error:', error);
  }
};

export const initAnalytics = (): void => {
  analyticsInitialized = true;
  ensureDataLayer();
};

export const isAnalyticsInitialized = (): boolean => analyticsInitialized;

export const identifyAnalytics = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  const user = getAnalyticsUser();
  const anonymousId = getOrCreateAnonymousId();
  const currentKey = user?.id ? `user:${user.id}` : `anon:${anonymousId}`;

  if (currentKey === lastIdentityKey) {
    return;
  }

  lastIdentityKey = currentKey;

  const payload: Record<string, unknown> = {
    event: 'user_identify',
    anonymous_id: anonymousId,
    user_status: getUserStatus(user),
  };

  if (user?.id) {
    payload.user_id = user.id;
    payload.backend_user_id = user.id;
  }

  pushToDataLayer(payload);
};

export const trackPageView = (params?: Record<string, unknown>): void => {
  if (typeof window === 'undefined') {
    return;
  }

  const allParams = getGlobalParams(params);
  const attributionData = getAttributionData();
  const isInitial = !getIsFirstPageViewFired();

  allParams.is_initial = isInitial;

  if (isInitial) {
    Object.assign(allParams, getInitialParams());

    const firstTouch = sanitizeTouch(attributionData.first_touch);
    if (firstTouch) {
      allParams.first_touch = firstTouch;
    }

    setFirstPageViewFired();
  }

  const lastTouch = sanitizeTouch(attributionData.last_touch);
  if (lastTouch) {
    allParams.last_touch = lastTouch;
  }

  pushToDataLayer({
    event: 'page_view',
    ...allParams,
  });
};

export const trackCustomEvent = (name: string, params?: Record<string, unknown>): void => {
  if (typeof window === 'undefined') {
    return;
  }

  pushToDataLayer({
    event: 'app_event',
    event_name: name,
    ...getGlobalParams(params),
  });
};

export const resetIdentityDedup = (): void => {
  lastIdentityKey = '';
};
